import React, { ChangeEvent, useContext, useState, useEffect } from "react";
import {
  Alert,
  FormControlLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Box,
  Checkbox,
} from "@mui/material";
import Button from "components/shared/Button/Button";
import { StyledTextField } from "views/Admin/Admin.styled";
import { GlobalContext } from "context/Provider";
import API from "api";
import { regOptionsInterface } from "context/initialState/Config";
import { usStates } from "../../../../helpers/variables";
import { StyledButton, ChartOptionLabels } from "./Configurations.styled";
import { configDisplayOptions } from "./Configurations.utils";
import Session from "../../../../helpers/session";

interface bulkDestinationInterface {
  name?: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone?: string;
  email?: string;
}

export default function Configurations() {
  const { config, configDispatch, settings, settingsDispatch } = useContext(GlobalContext);
  const [showSuccess, setSuccess] = useState<string>("");
  const [isChange, setIsChange] = useState<boolean>(false);
  const [groupName, setGroupName] = useState<string>("");
  const [groups, setGroups] = useState<string[]>(
    config?.regOptions.groups || []
  );
  const [bulkAddress, setBulkAddress] = useState<bulkDestinationInterface>({
    address: config?.orderOptions.bulkDestination.address || "",
    city: config?.orderOptions.bulkDestination.city || "",
    state: config?.orderOptions.bulkDestination.state || "",
    zip: config?.orderOptions.bulkDestination.zip || "",
    phone: config?.orderOptions.bulkDestination.phone || "",
    email: config?.orderOptions.bulkDestination.email || ""
  });
  const [checkboxOptions, setCheckboxOptions] = useState<Record<string, Record<string, boolean>>>({});

  const addGroup = () => {
    setGroups(groups.concat([groupName]));
    setGroupName("");
    setIsChange(true);
  };

  const removeGroup = (event: string) => {
    setGroups(
      groups.filter((name: string) => {
        return name !== event;
      })
    );
    setIsChange(true);
  };

  const updateGroupName = (event: ChangeEvent<HTMLInputElement>) => {
    setGroupName(event.target.value);
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    setBulkAddress({ ...bulkAddress, state: event.target.value });
    setIsChange(true);
  };

  const handleAddressChange = (event: ChangeEvent<HTMLInputElement>) => {
    setBulkAddress({ ...bulkAddress, [event.target.name]: event.target.value });
    setIsChange(true);
  };

  const handleSubmit = () => {

    API.kore.configurations
      .updateRegConfig({
        programs: config?.regOptions.programs || [],
        groups: groups
      } as regOptionsInterface)
      .then((regConf) => {
        configDispatch({
          type: "UPDATE_REGS",
          payload: {
            programs: config?.regOptions.programs || [],
            groups: groups
          }
        });
      })
      .catch((regErr) => console.log(regErr));
    API.kore.configurations
      // @ts-ignore
      .updateOrderingConfig({
        ...config?.orderOptions,
        bulkDestination: bulkAddress
      })
      .then((configs) => {
        configDispatch({
          type: "UPDATE_ORDERS",
          payload: { ...config?.orderOptions, bulkDestination: bulkAddress }
        });
        setSuccess("success");
        setIsChange(false);
      })
      .catch((err) => console.log(err));

    configDispatch({
      type: "UPDATE_GLOBAL_THRESHOLDS",
      payload: { ...checkboxOptions }
    });

    settingsDispatch({
      type: "UPDATE_DEFAULT_SETTINGS",
      payload: checkboxOptions
    });
  };

  const checkboxChange = (readingKey: string, subKey: string) => {
    setIsChange(true);

    setCheckboxOptions(prev => ({
      ...prev,
      [readingKey]: {
        ...prev[readingKey],
        [subKey]: !prev[readingKey]?.[subKey]
      }
    }));
  };

  const handleSelectAll = () => {
    const allTrue: Record<string, Record<string, boolean>> = {};

    configDisplayOptions.forEach(group => {
      allTrue[group.readingKey] = {};
      group.subTypes.forEach(option => {
        allTrue[group.readingKey][option.key] = true;
      });
    });

    setCheckboxOptions(allTrue);
    setIsChange(true);
  };

  const handleDeselectAll = () => {
    const allFalse: Record<string, Record<string, boolean>> = {};

    configDisplayOptions.forEach(group => {
      allFalse[group.readingKey] = {};
      group.subTypes.forEach(option => {
        allFalse[group.readingKey][option.key] = false;
      });
    });

    setCheckboxOptions(allFalse);
    setIsChange(true);
  };

  useEffect(() => {
    if (settings?.default?.data?.system?.settings) {
      setCheckboxOptions({ ...settings.default.data.system.settings });
    }

    const defaultSettings = settings?.default?.data;
    if (!defaultSettings) return;
    API.kore.settings
      .update("?entity=default", defaultSettings, Session.crackCookieToken("coordinator"))
      .catch((err: any) => console.log("Error updating client settings"));
  }, [settings?.default?.data]);

  return (
    <div>
      <Box display="flex" gap={6} mb={2}>
        <div>
          <h3 style={{ paddingLeft: '40px' }}>Site Configurations</h3>
          <Stack alignItems="flex-start" spacing={2} ml={15}>
            <h4>Groups list:</h4>
            {groups.map((group) => {
              return (
                <Stack direction="row" spacing={3} alignItems={"center"}>
                  <FormControlLabel
                    labelPlacement="start"
                    label={group}
                    control={<></>}
                  />
                  <Button
                    variant={"warning"}
                    onClick={() => {
                      removeGroup(group);
                    }}
                  >
                    -
                  </Button>
                </Stack>
              );
            })}
            <Stack direction="row" spacing={3} alignItems={"center"}>
              <FormControlLabel
                labelPlacement="start"
                label="Add Group"
                control={
                  <StyledTextField
                    onChange={updateGroupName}
                    value={groupName}
                    type="text"
                    placeholder="Group name"
                    size="small"
                  />
                }
              />
              <Button variant={"primary"} onClick={addGroup}>
                +
              </Button>
            </Stack>
            <br />
            <br />
            <h4>Bulk Orders Address:</h4>
            <TextField
              name="address"
              label="address"
              type="text"
              value={bulkAddress.address}
              onChange={handleAddressChange}
            />
            <TextField
              name="city"
              label="city"
              type="text"
              value={bulkAddress.city}
              onChange={handleAddressChange}
            />
            {/*<TextField name="state" label="state" type="text"/>*/}
            <Select
              name="state"
              label="State"
              sx={{ height: "55px" }}
              onChange={handleSelectChange}
              value={bulkAddress.state}
              variant="outlined"
              required
            >
              {usStates.map((S) => {
                return <MenuItem value={S}>{S}</MenuItem>;
              })}
            </Select>
            <TextField
              name="zip"
              label="zip"
              type="text"
              value={bulkAddress.zip}
              onChange={handleAddressChange}
            />
            <TextField
              name="phone"
              label="phone"
              type="text"
              value={bulkAddress.phone}
              onChange={handleAddressChange}
            />
            <TextField
              name="email"
              label="notification email"
              type="text"
              value={bulkAddress.email}
              onChange={handleAddressChange}
            />
          </Stack>
        </div>
        <div>
          <h3>Global Display Options</h3>
          <Stack alignItems="flex-start" spacing={2} ml={5}>
            <h4> Selecting items will reveal charts, thresholds, lists, and grid columns of the reading types. Selecting items will also reveal grid columns for risk alerts and latest readings </h4>
            <Stack direction="row" spacing={4}>
              <StyledButton onClick={handleSelectAll}> Select All </StyledButton>
              <StyledButton onClick={handleDeselectAll}> Deselect All </StyledButton>
            </Stack>
            {configDisplayOptions.map((option) => {
              return (
              <Box key={option.label}>
                <ChartOptionLabels>{option.label}:</ChartOptionLabels>
                <Stack
                  direction="row"
                  spacing={2}
                  pl={8}
                >
                  {option.subTypes.map((subType) => (
                    <FormControlLabel
                      key={subType.key}
                      control={
                        <Checkbox
                          id={subType.key}
                          checked={!!checkboxOptions[option.readingKey]?.[subType.key]}
                          onChange={() => checkboxChange(option.readingKey, subType.key)}
                        />
                      }
                      label={subType.label}
                    />
                  ))}
                </Stack>
              </Box>
      );
      })}
          </Stack>
        </div>
      </Box>
      <div>
        <Box sx={{ display: "flex", justifyContent: "center", pt: 10 }}>
          <Button
            variant="success"
            onClick={handleSubmit}
            margin={2}
            width="50%"
            disabled={!isChange}

          >
            Apply All Changes
          </Button>
        </Box>
        <br />
        {showSuccess === "success" ? (
          <Alert severity="success">
            Your configuration changes have been saved
          </Alert>
        ) : (
          ""
        )}
      </div>
    </div>
  );
}
